import React, { useEffect, useState } from 'react';
import api from '../api';

const EMPTY_FORM = {
  company_name: '', contact_name: '', email: '', phone: '',
  address: '', address_2: '', city: '', state: '', zip_code: '',
  country: '', industry: '', status: 'prospect', notes: ''
};

function CustomerModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState(customer || EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEdit = !!customer;

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const save = async () => {
    if (!form.company_name.trim()) return setError('Company name is required');
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/customers/${customer.id}`, form);
      } else {
        await api.post('/customers', form);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? 'Edit Customer' : 'New Customer'}</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-grid">
            <div className="form-group">
              <label>Company Name *</label>
              <input value={form.company_name} onChange={e => set('company_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Contact Name</label>
              <input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="prospect">Prospect</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="form-group">
              <label>Industry</label>
              <input value={form.industry} onChange={e => set('industry', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Address</label>
            <input value={form.address} onChange={e => set('address', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Address 2</label>
            <input value={form.address_2} onChange={e => set('address_2', e.target.value)} />
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>City</label>
              <input value={form.city} onChange={e => set('city', e.target.value)} />
            </div>
            <div className="form-group">
              <label>County / State</label>
              <input value={form.state} onChange={e => set('state', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Postcode</label>
              <input value={form.zip_code} onChange={e => set('zip_code', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Country</label>
              <input value={form.country} onChange={e => set('country', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Customer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CustomerDetail({ customer, onClose, onEdit, onDelete }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{customer.company_name}</span>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Contact</div>
              <div>{customer.contact_name || '—'}</div>
              <div style={{ color: 'var(--text-secondary)' }}>{customer.email || '—'}</div>
              <div style={{ color: 'var(--text-secondary)' }}>{customer.phone || '—'}</div>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
              <span className={`badge badge-${customer.status}`}>{customer.status}</span>
              {customer.industry && <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: 12 }}>{customer.industry}</div>}
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Address</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.8 }}>
                {[customer.address, customer.address_2, customer.city, customer.state, customer.zip_code, customer.country].filter(Boolean).join(', ') || '—'}
              </div>
            </div>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>Customer Since</div>
              <div style={{ color: 'var(--text-secondary)' }}>
                {customer.created_at ? new Date(customer.created_at).toLocaleDateString('en-GB') : '—'}
              </div>
            </div>
          </div>
          {customer.notes && (
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.7, background: 'var(--bg-primary)', padding: 12, borderRadius: 4 }}>{customer.notes}</div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-danger btn-sm" onClick={onDelete}>Delete</button>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          <button className="btn btn-primary" onClick={onEdit}>Edit</button>
        </div>
      </div>
    </div>
  );
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/customers')
      .then(res => { setCustomers(res.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = customers.filter(c => {
    const matchSearch = !search ||
      c.company_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.contact_name?.toLowerCase().includes(search.toLowerCase()) ||
      c.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    await api.delete(`/customers/${id}`);
    setSelected(null);
    load();
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">CUSTO<span>MERS</span></h2>
        <button className="btn btn-primary" onClick={() => setShowNew(true)}>+ New Customer</button>
      </div>

      <div className="search-bar">
        <input
          placeholder="Search company, contact, email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="prospect">Prospect</option>
          <option value="inactive">Inactive</option>
        </select>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>
          {filtered.length} records
        </span>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading">Loading customers...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">No customers found</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Industry</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} onClick={() => setSelected(c)}>
                    <td style={{ fontWeight: 600 }}>{c.company_name}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{c.contact_name || '—'}</td>
                    <td style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{c.email || '—'}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{c.phone || '—'}</td>
                    <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{c.industry || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && !editing && (
        <CustomerDetail
          customer={selected}
          onClose={() => setSelected(null)}
          onEdit={() => setEditing(selected)}
          onDelete={() => handleDelete(selected.id)}
        />
      )}

      {editing && (
        <CustomerModal
          customer={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setSelected(null); load(); }}
        />
      )}

      {showNew && (
        <CustomerModal
          onClose={() => setShowNew(false)}
          onSaved={() => { setShowNew(false); load(); }}
        />
      )}
    </div>
  );
}